import type React from "react";
import { useEffect, useState } from "react";
import {
	type AudioAnalysisConfig,
	DEFAULT_AUDIO_ANALYSIS_CONFIG,
} from "../../../audio/audio-analyzer";
import { ErrorSeverity } from "../../../error-handling/error-handler";
import { errorUI } from "../../../error-handling/index";
import type { Preset } from "../../../interfaces/preset-management";
import type { MainService } from "../../../main-service";
import {
	type AutoCommentConfig,
	type CommenterDefinition,
	type CommenterType,
	DEFAULT_COMMENTER_DEFINITIONS,
} from "../../../types/core";
import { Modal } from "./Modal";
import { PreferenceAdvancedTab } from "./preferences/PreferenceAdvancedTab";
import { PreferenceApiKeysTab } from "./preferences/PreferenceApiKeysTab";
import { PreferenceAutoCommentTab } from "./preferences/PreferenceAutoCommentTab";
import { PreferenceDataTab } from "./preferences/PreferenceDataTab";
import { PreferenceGeneralTab } from "./preferences/PreferenceGeneralTab";
import { PreferenceNotificationsTab } from "./preferences/PreferenceNotificationsTab";
import {
	PREFERENCE_TAB_IDS,
	PREFERENCE_TAB_PANEL_IDS,
	type PreferenceTab,
	PreferenceTabNav,
} from "./preferences/PreferenceTabNav";

interface PreferenceModalProps {
	isOpen: boolean;
	onClose: () => void;
	service: MainService | null;
}

export const PreferenceModal: React.FC<PreferenceModalProps> = ({
	isOpen,
	onClose,
	service,
}) => {
	const [activeTab, setActiveTab] = useState<PreferenceTab>("general");
	const [apiKeys, setApiKeys] = useState({ gemini: "" });
	const [audioConfig, setAudioConfig] = useState<AudioAnalysisConfig>(
		service?.getAudioConfig() || DEFAULT_AUDIO_ANALYSIS_CONFIG,
	);
	const [basicPolicy, setBasicPolicy] = useState("");
	const [maxComments, setMaxComments] = useState(3);
	const [commenters, setCommenters] = useState<CommenterDefinition[]>([]);
	const [userPresets, setUserPresets] = useState<Preset[]>([]);
	const [showGeminiKey, setShowGeminiKey] = useState(false);
	const [lowConfidenceThreshold, setLowConfidenceThreshold] = useState(0.9);
	const [editingCommenter, setEditingCommenter] =
		useState<CommenterDefinition | null>(null);
	const [isSavingPreset, setIsSavingPreset] = useState(false);
	const [editingPreset, setEditingPreset] = useState<Preset | null>(null);
	const [newPresetName, setNewPresetName] = useState("");
	const [newPresetDescription, setNewPresetDescription] = useState("");
	const [notificationsEnabled, setNotificationsEnabled] = useState(false);
	const [autoCommentConfig, setAutoCommentConfig] = useState<
		Partial<AutoCommentConfig>
	>({});

	useEffect(() => {
		if (isOpen && service) {
			const loadData = async () => {
				const prefs = await service.getPreferences();
				setApiKeys({ gemini: prefs.apiKeys?.summaryApiKey || "" });
				setBasicPolicy(prefs.policyConfig?.basicPolicy || "");
				const currentCommenters =
					prefs.commenterDefinitions || DEFAULT_COMMENTER_DEFINITIONS;
				setCommenters(currentCommenters);
				setLowConfidenceThreshold(prefs.lowConfidenceThreshold ?? 0.9);

				let maxCount = prefs.commentMaxCount ?? 3;
				if (maxCount > currentCommenters.length) {
					maxCount = Math.max(1, currentCommenters.length);
					await service.updateCommentMaxCount(maxCount);
				}
				setMaxComments(maxCount);
				setUserPresets((await service.getPresets()) || []);
				setAudioConfig(service.getAudioConfig());
				setNotificationsEnabled(prefs.notificationsEnabled ?? false);
				setAutoCommentConfig(prefs.autoCommentConfig ?? {});
			};
			loadData();
		}
	}, [isOpen, service]);

	useEffect(() => {
		if (service && commenters.length > 0 && maxComments > commenters.length) {
			const newMax = commenters.length;
			setMaxComments(newMax);
			service.updateCommentMaxCount(newMax);
		}
	}, [commenters, maxComments, service]);

	const handleApplyPreset = async (id: string, name: string) => {
		if (!service) return;
		errorUI.showConfirmation(
			`プリセット「${name}」を適用しますか？\n現在の設定が上書きされます。`,
			async () => {
				await service.applyPreset(id);
				const prefs = await service.getPreferences();
				const newCommenters =
					prefs.commenterDefinitions || DEFAULT_COMMENTER_DEFINITIONS;
				setCommenters(newCommenters);
				setBasicPolicy(prefs.policyConfig?.basicPolicy || "");
				const presetMax = prefs.commentMaxCount ?? newCommenters.length;
				const newMax = Math.max(1, Math.min(presetMax, newCommenters.length));
				setMaxComments(newMax);
				await service.updateCommentMaxCount(newMax);
				errorUI.showNotification(
					`プリセット「${name}」を適用しました。`,
					ErrorSeverity.LOW,
				);
			},
		);
	};

	const handleExport = async () => {
		if (!service) return;
		const data = await service.exportData();
		const blob = new Blob([data], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `monolog-live-data-${new Date().toISOString().split("T")[0]}.json`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !service) return;
		const reader = new FileReader();
		reader.onload = async (event) => {
			const content = event.target?.result as string;
			try {
				errorUI.showConfirmation(
					"データを復元しますか？\n現在の設定や履歴がインポートされたデータで上書き・統合されます。",
					async () => {
						await service.importData(content);
						errorUI.showNotification(
							"データの復元が完了しました。ページを再読み込みします。",
							ErrorSeverity.LOW,
						);
						setTimeout(() => window.location.reload(), 1500);
					},
				);
			} catch (err) {
				errorUI.showNotification(
					"データの復元に失敗しました。ファイル形式が正しいか確認してください。",
					ErrorSeverity.HIGH,
				);
				console.error("Import error:", err);
			}
		};
		reader.readAsText(file);
		e.target.value = "";
	};

	const handleAddCommenter = () => {
		setEditingCommenter({
			id: `user_${Date.now()}` as CommenterType,
			name: "",
			description: "",
			icon: "💬",
			color: "#4a90e2",
		});
	};

	const handleSaveCommenter = async () => {
		if (!editingCommenter || !service) return;
		const exists = commenters.some((c) => c.id === editingCommenter.id);
		const newCommenters = exists
			? commenters.map((c) =>
					c.id === editingCommenter.id ? editingCommenter : c,
				)
			: [...commenters, editingCommenter];
		setCommenters(newCommenters);
		await service.updateCommenterDefinitions(newCommenters);
		setEditingCommenter(null);
		errorUI.showNotification("コメント担当を保存しました。", ErrorSeverity.LOW);
	};

	const handleDeleteCommenter = async (id: string) => {
		if (!service) return;
		errorUI.showConfirmation("このコメント担当を削除しますか？", async () => {
			const newCommenters = commenters.filter((c) => c.id !== id);
			setCommenters(newCommenters);
			await service.updateCommenterDefinitions(newCommenters);
			errorUI.showNotification(
				"コメント担当を削除しました。",
				ErrorSeverity.LOW,
			);
		});
	};

	const handleSaveCurrentAsPreset = async () => {
		if (!service || !newPresetName.trim()) return;
		try {
			await service.saveCurrentAsPreset(newPresetName, newPresetDescription);
			setIsSavingPreset(false);
			setNewPresetName("");
			setNewPresetDescription("");
			setUserPresets((await service.getPresets()) || []);
			errorUI.showNotification(
				"現在の設定をプリセットとして保存しました。",
				ErrorSeverity.LOW,
			);
		} catch (error) {
			errorUI.showNotification(
				"プリセットの保存に失敗しました。",
				ErrorSeverity.HIGH,
			);
			console.error("Save preset error:", error);
		}
	};

	const handleDeletePreset = async (id: string, name: string) => {
		if (!service) return;
		errorUI.showConfirmation(
			`プリセット「${name}」を削除しますか？`,
			async () => {
				try {
					await service.deletePreset(id);
					setUserPresets((await service.getPresets()) || []);
					errorUI.showNotification(
						`プリセット「${name}」を削除しました。`,
						ErrorSeverity.LOW,
					);
				} catch (error) {
					errorUI.showNotification(
						"プリセットの削除に失敗しました。",
						ErrorSeverity.HIGH,
					);
					console.error("Delete preset error:", error);
				}
			},
		);
	};

	const handleUpdatePreset = async () => {
		if (!service || !editingPreset || !newPresetName.trim()) return;
		try {
			await service.updatePreset(editingPreset.id, {
				name: newPresetName,
				description: newPresetDescription,
			});
			setEditingPreset(null);
			setNewPresetName("");
			setNewPresetDescription("");
			setUserPresets((await service.getPresets()) || []);
			errorUI.showNotification("プリセットを更新しました。", ErrorSeverity.LOW);
		} catch (error) {
			errorUI.showNotification(
				"プリセットの更新に失敗しました。",
				ErrorSeverity.HIGH,
			);
			console.error("Update preset error:", error);
		}
	};

	if (!isOpen) return null;

	return (
		<Modal title="設定" isOpen={isOpen} onClose={onClose}>
			<div className="flex h-full max-[600px]:flex-col">
				<PreferenceTabNav
					activeTab={activeTab}
					apiKeyConfigured={Boolean(apiKeys.gemini)}
					onChangeTab={setActiveTab}
				/>
				<div className="flex-1 p-6 overflow-y-auto max-[600px]:p-4">
					{activeTab === "general" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.general}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.general}
						>
							<PreferenceGeneralTab
								basicPolicy={basicPolicy}
								maxComments={maxComments}
								commenters={commenters}
								userPresets={userPresets}
								editingCommenter={editingCommenter}
								isSavingPreset={isSavingPreset}
								editingPreset={editingPreset}
								newPresetName={newPresetName}
								newPresetDescription={newPresetDescription}
								onApplyPreset={handleApplyPreset}
								onSetEditingPreset={setEditingPreset}
								onSetIsSavingPreset={setIsSavingPreset}
								onSetNewPresetName={setNewPresetName}
								onSetNewPresetDescription={setNewPresetDescription}
								onDeletePreset={handleDeletePreset}
								onUpdatePreset={handleUpdatePreset}
								onSaveCurrentAsPreset={handleSaveCurrentAsPreset}
								onSetBasicPolicy={(policy) => {
									setBasicPolicy(policy);
									service?.updatePolicyConfig(policy);
								}}
								onAddCommenter={handleAddCommenter}
								onEditCommenter={(commenter) =>
									setEditingCommenter({ ...commenter })
								}
								onDeleteCommenter={handleDeleteCommenter}
								onSetMaxComments={(val) => {
									setMaxComments(val);
									service?.updateCommentMaxCount(val);
								}}
								onSetEditingCommenter={setEditingCommenter}
								onSaveCommenter={handleSaveCommenter}
							/>
						</div>
					)}
					{activeTab === "apikeys" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.apikeys}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.apikeys}
						>
							<PreferenceApiKeysTab
								geminiKey={apiKeys.gemini}
								showGeminiKey={showGeminiKey}
								onToggleGeminiKey={() => setShowGeminiKey((prev) => !prev)}
								onChangeGeminiKey={(val) => {
									setApiKeys((prev) => ({ ...prev, gemini: val }));
									service?.updateApiKeys({ gemini: val });
								}}
							/>
						</div>
					)}
					{activeTab === "data" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.data}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.data}
						>
							<PreferenceDataTab
								onExport={handleExport}
								onOpenImport={() =>
									document.getElementById("import-input")?.click()
								}
								onImport={handleImport}
							/>
						</div>
					)}
					{activeTab === "notifications" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.notifications}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.notifications}
						>
							<PreferenceNotificationsTab
								notificationsEnabled={notificationsEnabled}
								onUpdateNotificationsEnabled={async (enabled) => {
									setNotificationsEnabled(enabled);
									await service?.updateNotificationsEnabled(enabled);
								}}
							/>
						</div>
					)}
					{activeTab === "autocomment" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.autocomment}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.autocomment}
						>
							<PreferenceAutoCommentTab
								autoCommentConfig={autoCommentConfig}
								onUpdateAutoCommentConfig={async (config) => {
									setAutoCommentConfig((prev) => ({ ...prev, ...config }));
									await service?.updateAutoCommentConfig(config);
								}}
							/>
						</div>
					)}
					{activeTab === "advanced" && (
						<div
							id={PREFERENCE_TAB_PANEL_IDS.advanced}
							role="tabpanel"
							aria-labelledby={PREFERENCE_TAB_IDS.advanced}
						>
							<PreferenceAdvancedTab
								audioConfig={audioConfig}
								lowConfidenceThreshold={lowConfidenceThreshold}
								onUpdateAudioConfig={(config) => {
									setAudioConfig((prev) => ({ ...prev, ...config }));
									service?.updateAudioConfig(config);
								}}
								onUpdateLowConfidenceThreshold={(threshold) => {
									setLowConfidenceThreshold(threshold);
									service?.updateTranscriptionConfig(threshold);
								}}
							/>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};
